import { baseApi } from '@/features/api/baseApi';
import directus, { type DirectusErrorType, type RequestQuery } from '@/features/api/directus';
import {
  buildCountFn,
  buildReadManyFn,
  buildUpdateFn,
} from '@/features/api/endpoints/createDirectusEndpoints';
import type { FileSubmissionStatus } from '@/models/fileSubmission.type';
import type { FileSubmission, FileSubmissionDirectusFiles } from '@/models/types';
import { createItem } from '@directus/sdk';
import type { QueryReturnValue } from '@reduxjs/toolkit/query';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type Result<T> = QueryReturnValue<T, DirectusErrorType, {}>;

export type FileSubmissionCreate = Omit<Partial<FileSubmission>, 'files'> & {
  files: Array<Pick<FileSubmissionDirectusFiles, 'directus_files_id'>>;
};

export type FileSubmissionUpdate = {
  status: FileSubmissionStatus;
};

const createFileSubmissionFn = async (
  body: FileSubmissionCreate,
): Promise<Result<FileSubmission>> => {
  try {
    const data = await directus.request<FileSubmission>(
      createItem('FileSubmission', body as never),
    );
    return { data };
  } catch (error: unknown) {
    return { error: error as DirectusErrorType };
  }
};

const fileSubmissionApi = baseApi
  .enhanceEndpoints({
    addTagTypes: ['fileSubmission', 'fileSubmissions'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getFileSubmissions: builder.query<FileSubmission[], RequestQuery<FileSubmission>>({
        queryFn: buildReadManyFn<FileSubmission>('FileSubmission'),
        providesTags: ['fileSubmissions'],
      }),
      getFileSubmissionCount: builder.query<number, RequestQuery<FileSubmission>>({
        queryFn: buildCountFn<FileSubmission>('FileSubmission'),
        providesTags: ['fileSubmissions'],
      }),
      createFileSubmission: builder.mutation<FileSubmission, FileSubmissionCreate>({
        queryFn: createFileSubmissionFn,
        invalidatesTags: ['fileSubmissions'],
      }),
      updateFileSubmission: builder.mutation<
        FileSubmission,
        { id: number; body: FileSubmissionUpdate }
      >({
        queryFn: buildUpdateFn<FileSubmission, number>('FileSubmission'),
        invalidatesTags: ['fileSubmission', 'fileSubmissions'],
      }),
    }),
  });

export const {
  useGetFileSubmissionsQuery,
  useGetFileSubmissionCountQuery,
  useCreateFileSubmissionMutation,
  useUpdateFileSubmissionMutation,
} = fileSubmissionApi;
